// Car, taxi and bus models.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../lib/math.js';
import { rnd } from '../lib/random.js';
import { M, P, Ph, add, canvasTex } from '../lib/factory.js';
import { plateTex } from '../textures/signs.js';
import { profile } from './scooter.js';

export let carSeat, amberMat, carHead, carTail, CAR, makeCar, cars;

export function buildCars() {
  makeCar = function makeCar(type, color, taxi) {
    const T = CAR[type],
      g = new THREE.Group(),
      body = Ph(color, { roughness: 0.32, metalness: 0.55 }),
      hw = T.w / 2 + 0.06,
      bz = T.len / 2 + 0.06;
    // generous corner radii: softer, toy-like silhouettes instead of sharp corners
    add(profile(T.body, T.w, 0.06, type === 'bus' ? 0.3 : 0.28), body, 0, 0, 0, g);
    // the glass stands just proud of the body sides so the side windows read
    if (T.glass) {
      add(profile(T.glass, T.wide ? T.w + 0.16 : T.w + 0.06, 0.04, 0.16), carGlass, 0, 0, 0, g);
      // dark rubber frame round the glass: the same outline grown a little, sitting just under the glass
      const cx = T.glass.reduce((a, p) => a + p[0], 0) / T.glass.length,
        cy = T.glass.reduce((a, p) => a + p[1], 0) / T.glass.length;
      const grown = T.glass.map(([z, y]) => {
        const dz = z - cx,
          dy = y - cy,
          l = Math.hypot(dz, dy) || 1;
        return [z + (dz / l) * 0.035, y + (dy / l) * 0.035];
      });
      add(profile(grown, T.wide ? T.w + 0.14 : T.w + 0.04, 0.04, 0.17), trimMat, 0, 0, 0, g);
      // side windows read as glass: black pillars split them, and white diagonal glints sit on each pane
      const gx = (T.wide ? T.w + 0.16 : T.w + 0.06) / 2 + 0.045,
        zs = T.glass.map((p) => p[0]),
        ys = T.glass.map((p) => p[1]);
      const zf = Math.max(...zs) - 0.3,
        zr = Math.min(...zs) + 0.12,
        y0 = Math.min(...ys) + 0.04,
        y1 = Math.max(...ys) - 0.04;
      const posts = type === 'van' ? [] : type === 'sedan' ? [T.pillar] : [T.pillar];
      for (const sx of [-1, 1]) {
        for (const pz of posts)
          add(
            new THREE.BoxGeometry(0.02, y1 - y0 + 0.06, 0.07),
            trimMat,
            sx * gx,
            (y0 + y1) / 2,
            pz,
            g,
          );
        const panes = [zf, ...posts, zr].sort((a, b) => b - a);
        for (let k = 0; k + 1 < panes.length; k++) {
          const mid = (panes[k] + panes[k + 1]) / 2,
            w = panes[k] - panes[k + 1];
          if (w < 0.2) continue;
          // the cabin seen through the glass: a darker lower half and a headrest silhouette
          add(
            new THREE.PlaneGeometry(w * 0.9, (y1 - y0) * 0.5).rotateY((sx * PI) / 2),
            cabinMat,
            sx * (gx + 0.002),
            y0 + (y1 - y0) * 0.25,
            mid,
            g,
          ).castShadow = false;
          add(
            new THREE.CircleGeometry(0.07, 16).scale(1, 1.15, 1).rotateY((sx * PI) / 2),
            cabinMat,
            sx * (gx + 0.003),
            y0 + (y1 - y0) * 0.55,
            mid - w * 0.15,
            g,
          ).castShadow = false;
          for (const [o, gw] of [
            [-0.12, 0.07],
            [0.04, 0.03],
          ]) {
            const m = add(
              new THREE.PlaneGeometry(gw, (y1 - y0) * 0.95).rotateX(0.55).rotateY((sx * PI) / 2),
              glintMat,
              sx * (gx + 0.004),
              (y0 + y1) / 2,
              mid + o * w,
              g,
            );
            m.castShadow = false;
          }
        }
      }
    }
    if (T.roof) add(profile(T.roof, T.w * 0.92, 0.05, 0.06), body, 0, 0, 0, g);
    if (T.pillar !== undefined)
      add(
        new RoundedBoxGeometry(T.w * 0.92 + 0.07, 0.34, 0.07, 3, 0.03),
        body,
        0,
        0.79,
        T.pillar,
        g,
      );
    if (T.glass && type !== 'bus') {
      // door shut lines on both sides: an outline per door from the sill up to the window line
      const seam = (z, y0, y1) => {
        for (const sx of [-1, 1])
          add(
            new THREE.BoxGeometry(0.012, y1 - y0, 0.016),
            trimMat,
            sx * (hw + 0.003),
            (y0 + y1) / 2,
            z,
            g,
          );
      };
      const sill = (z0, z1, y) => {
        for (const sx of [-1, 1])
          add(
            new THREE.BoxGeometry(0.012, 0.016, Math.abs(z1 - z0)),
            trimMat,
            sx * (hw + 0.003),
            y,
            (z0 + z1) / 2,
            g,
          );
      };
      const wy = T.glass[0][1] - 0.02,
        dz = T.glass[0][0] - 0.02;
      if (type === 'van') {
        seam(dz, 0.24, wy);
        seam(dz - 0.62, 0.24, 1.05);
        seam(-0.9, 0.24, 1.05);
        sill(dz - 0.62, -0.9, 0.24);
      } else {
        const pz = T.pillar ?? 0.3,
          rz2 = T.glass.at(-1)[0] + 0.06;
        seam(dz, 0.24, wy);
        seam(pz, 0.24, wy);
        sill(dz, pz, 0.24);
        if (type === 'sedan') {
          seam(rz2, 0.24, wy);
          sill(pz, rz2, 0.24);
        }
      }
      const fz = T.glass[0][0],
        gy = T.glass[0][1],
        rz = T.glass.at(-1)[0];
      for (const sx of [-1, 1]) {
        add(
          new RoundedBoxGeometry(0.3, 0.36, 0.3, 2, 0.06),
          carSeat,
          sx * 0.21,
          0.62,
          fz - 0.62,
          g,
        );
        add(
          new RoundedBoxGeometry(0.28, 0.12, 0.1, 2, 0.04),
          carSeat,
          sx * 0.21,
          0.88,
          fz - 0.76,
          g,
        );
        add(
          new RoundedBoxGeometry(0.012, 0.012, 0.42, 1, 0.004),
          trimMat,
          sx * 0.17,
          gy + 0.05,
          fz - 0.1,
          g,
        ).rotation.set(-0.6, sx * 0.6, 0);
        add(
          new RoundedBoxGeometry(0.07, 0.05, 0.04, 1, 0.015),
          amberMat,
          sx * (hw - 0.05),
          0.47,
          bz - 0.02,
          g,
        );
      }
      if (type !== 'van')
        add(new RoundedBoxGeometry(T.w * 0.7, 0.3, 0.3, 2, 0.06), carSeat, 0, 0.62, rz + 0.42, g);
      add(
        new RoundedBoxGeometry(T.w * 0.8, 0.1, 0.22, 2, 0.03),
        carSeat,
        0,
        gy + 0.02,
        fz - 0.2,
        g,
      );
      add(
        new THREE.TorusGeometry(0.09, 0.018, 8, 20),
        trimMat,
        -0.21,
        gy + 0.12,
        fz - 0.36,
        g,
      ).rotation.x = -0.5;
      if (rnd() < 0.4 && T.roof)
        add(
          new THREE.CylinderGeometry(0.006, 0.01, 0.32, 6),
          trimMat,
          0.2,
          1.16,
          T.roof.at(-1)[0] + 0.1,
          g,
        ).rotation.x = -0.4;
    }
    if (type !== 'bus')
      add(
        new THREE.CylinderGeometry(0.035, 0.035, 0.16, 10).rotateX(PI / 2),
        hubMat,
        hw - 0.25,
        0.2,
        -bz - 0.02,
        g,
      );
    if (type === 'bus') {
      add(new RoundedBoxGeometry(2 * hw + 0.01, 0.66, 5.42, 3, 0.1), trimMat, 0, 1.15, -0.2, g); // rubber window band
      add(new RoundedBoxGeometry(2 * hw + 0.02, 0.56, 5.3, 3, 0.1), busGlass, 0, 1.15, -0.2, g);
      // windscreen and rear window, framed
      add(
        new RoundedBoxGeometry(2 * hw - 0.12, 0.82, 0.05, 3, 0.06),
        trimMat,
        0,
        1.12,
        bz - 0.02,
        g,
      );
      add(new RoundedBoxGeometry(2 * hw - 0.2, 0.72, 0.06, 3, 0.05), carGlass, 0, 1.13, bz, g);
      add(new RoundedBoxGeometry(2 * hw - 0.3, 0.5, 0.05, 3, 0.05), trimMat, 0, 1.2, -bz + 0.02, g);
      add(new RoundedBoxGeometry(2 * hw - 0.38, 0.42, 0.06, 3, 0.04), carGlass, 0, 1.2, -bz, g);
      // two folding doors on the kerb side (right of travel = -x): dark frame, glass halves, a centre split
      for (const [dzc, dw] of [
        [2.66, 0.56],
        [-0.1, 0.78],
      ]) {
        // front door ahead of the front axle, wide middle door
        add(new RoundedBoxGeometry(0.03, 1.28, dw, 2, 0.02), trimMat, -hw - 0.006, 0.86, dzc, g);
        for (const o of [-1, 1])
          add(
            new RoundedBoxGeometry(0.02, 1.12, dw / 2 - 0.07, 2, 0.02),
            carGlass,
            -hw - 0.02,
            0.88,
            dzc + (o * dw) / 4,
            g,
          );
        add(new THREE.BoxGeometry(0.03, 1.18, 0.03), trimMat, -hw - 0.025, 0.88, dzc, g);
      }
      add(
        new RoundedBoxGeometry(T.w, 0.14, 5.2, 2, 0.05),
        P(0xf2f0ea, { roughness: 0.3 }),
        0,
        1.8,
        0,
        g,
      );
      add(
        new RoundedBoxGeometry(2 * hw + 0.03, 0.12, 6.05, 2, 0.04),
        P(0xf2f0ea, { roughness: 0.3 }),
        0,
        0.62,
        0,
        g,
      );
      add(
        new RoundedBoxGeometry(0.7, 0.14, 0.03, 2, 0.01),
        M(0xffa62b, { emissive: 0xff9a1a, emissiveIntensity: 1, flatShading: false }),
        0,
        1.5,
        bz + 0.01,
        g,
      );
    }
    if (taxi) {
      for (const sx of [-1, 1])
        for (const z of [0.28, -0.32])
          add(
            new RoundedBoxGeometry(0.02, 0.3, 0.56, 2, 0.008),
            yellowMat,
            sx * (hw + 0.002),
            0.42,
            z,
            g,
          );
      add(
        new RoundedBoxGeometry(0.32, 0.1, 0.16, 2, 0.03),
        M(0x3ddc84, { emissive: 0x3ddc84, emissiveIntensity: 1, flatShading: false }),
        0,
        1.07,
        -0.25,
        g,
      );
    }
    for (const s of [1, -1]) {
      add(
        new RoundedBoxGeometry(2 * hw + 0.02, 0.13, 0.12, 3, 0.05),
        trimMat,
        0,
        0.26,
        s * (bz + 0.005),
        g,
      );
      add(
        new RoundedBoxGeometry(0.32, 0.09, 0.015, 1, 0.005),
        plateMat,
        0,
        0.3,
        s * (bz + 0.06),
        g,
      );
    }
    if (type !== 'bus')
      add(new RoundedBoxGeometry(T.w * 0.45, 0.1, 0.03, 2, 0.012), trimMat, 0, 0.4, bz + 0.01, g);
    for (const sx of [-1, 1]) {
      add(
        new RoundedBoxGeometry(0.2, 0.08, 0.04, 2, 0.015),
        carHead,
        sx * (hw - 0.17),
        0.46,
        bz + 0.005,
        g,
      );
      add(
        new RoundedBoxGeometry(0.18, 0.1, 0.04, 2, 0.015),
        carTail,
        sx * (hw - 0.14),
        0.46,
        -bz - 0.005,
        g,
      );
      if (T.glass && type !== 'bus') {
        const mz = (T.glass[0][0] + T.glass[1][0]) / 2 - 0.05;
        add(new RoundedBoxGeometry(0.1, 0.07, 0.12, 2, 0.02), body, sx * (hw + 0.05), 0.78, mz, g);
        add(
          new RoundedBoxGeometry(0.012, 0.3, 0.012, 1, 0.004),
          trimMat,
          sx * (hw + 0.003),
          0.42,
          T.pillar ?? 0.3,
          g,
        );
        for (const hz of [T.pillar ?? 0.3])
          add(
            new RoundedBoxGeometry(0.02, 0.03, 0.1, 1, 0.008),
            hubMat,
            sx * (hw + 0.01),
            0.55,
            hz + 0.25,
            g,
          );
      }
      for (const z of T.wz) {
        add(
          new THREE.CylinderGeometry(
            T.r + 0.06,
            T.r + 0.06,
            2 * hw + 0.012,
            32,
            1,
            false,
            0,
            PI,
          ).rotateZ(PI / 2),
          trimMat,
          0,
          T.r,
          z,
          g,
        );
        const wg = new THREE.Group();
        wg.position.set(sx * (hw - 0.03), T.r, z);
        g.add(wg);
        add(
          new THREE.TorusGeometry(T.r * 0.72, T.r * 0.3, 12, 32).rotateY(PI / 2),
          carTire,
          0,
          0,
          0,
          wg,
        );
        add(
          new THREE.CylinderGeometry(T.r * 0.62, T.r * 0.62, 0.1, 28).rotateZ(PI / 2),
          hubMat,
          sx * 0.01,
          0,
          0,
          wg,
        );
        add(
          new THREE.CylinderGeometry(T.r * 0.22, T.r * 0.22, 0.11, 16).rotateZ(PI / 2),
          trimMat,
          sx * 0.015,
          0,
          0,
          wg,
        );
      }
    }
    return g;
  };

  // cars from rounded extruded profiles: hatchbacks, sedans, vans, Barcelona taxis and red TMB buses
  // painted car glass: a cool blue tint, brighter towards the top where it catches the sky, with two white diagonal
  // glints. The extruded window caps take their UVs straight from the side profile (units), so one tile spans a window.
  const glassTex = canvasTex(128, 128, (c) => {
    const g = c.createLinearGradient(0, 0, 0, 128);
    g.addColorStop(0, '#e2f2fb');
    g.addColorStop(0.45, '#8fbad4');
    g.addColorStop(1, '#4b6f8c');
    c.fillStyle = g;
    c.fillRect(0, 0, 128, 128);
    c.fillStyle = 'rgba(255,255,255,.75)';
    for (const [x, w] of [
      [30, 16],
      [58, 6],
    ]) {
      c.beginPath();
      c.moveTo(x, 0);
      c.lineTo(x + w, 0);
      c.lineTo(x + w - 40, 128);
      c.lineTo(x - 40, 128);
      c.fill();
    }
  });
  glassTex.repeat.set(1.1, 1.6);
  glassTex.offset.set(0.1, -0.9);
  const carGlass = P(0xffffff, {
    map: glassTex,
    emissive: 0x1a3550,
    emissiveIntensity: 0.15,
    roughness: 0.08,
    metalness: 0.2,
    envMapIntensity: 1.2,
  }); // opaque: the cabin shade and glints are drawn over it
  // bus windows: the same painted glass, its UVs per box face (0..1), so a softer tiling
  const busGlassTex = canvasTex(128, 64, (c) => {
    const g = c.createLinearGradient(0, 0, 0, 64);
    g.addColorStop(0, '#d6ecf8');
    g.addColorStop(0.5, '#7fa9c6');
    g.addColorStop(1, '#3d5d78');
    c.fillStyle = g;
    c.fillRect(0, 0, 128, 64);
    c.fillStyle = 'rgba(255,255,255,.6)';
    for (let x = 10; x < 128; x += 42) {
      c.beginPath();
      c.moveTo(x, 0);
      c.lineTo(x + 10, 0);
      c.lineTo(x - 6, 64);
      c.lineTo(x - 16, 64);
      c.fill();
    }
    c.fillStyle = '#1d2433';
    for (let x = 0; x < 128; x += 21) c.fillRect(x, 0, 2, 64);
  });
  busGlassTex.repeat.set(1, 1);
  const cabinMat = new THREE.MeshBasicMaterial({
    color: 0x0e1a26,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const glintMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const busGlass = P(0xffffff, {
    map: busGlassTex,
    emissive: 0x2a4a66,
    emissiveIntensity: 0.25,
    roughness: 0.08,
    metalness: 0.2,
    envMapIntensity: 1.2,
    transparent: true,
    opacity: 0.88,
  });
  carSeat = P(0x2b2d31, { roughness: 0.8, metalness: 0, envMapIntensity: 0.2 });
  amberMat = M(0xffa21a, { emissive: 0xff8a00, emissiveIntensity: 0.3, flatShading: false });
  carHead = M(0xfff6e0, { emissive: 0xfff2cc, emissiveIntensity: 0.2, flatShading: false });
  carTail = M(0xd8352a, { emissive: 0xff2a1a, emissiveIntensity: 0.3, flatShading: false });
  const hubMat = P(0xc9ced4, { roughness: 0.18, metalness: 0.95 }),
    trimMat = P(0x17181a, { roughness: 0.55, metalness: 0.1, envMapIntensity: 0.4 });
  const carTire = P(0x1b1b1b, { roughness: 0.85, metalness: 0, envMapIntensity: 0.2 }),
    plateMat = M(0xffffff, { map: plateTex('4271 JCP'), flatShading: false }),
    yellowMat = P(0xf2c014, { roughness: 0.25, metalness: 0.4 });
  CAR = {
    hatch: {
      len: 2.1,
      w: 0.9,
      r: 0.2,
      wz: [0.68, -0.68],
      body: [
        [-1.05, 0.18],
        [1.03, 0.18],
        [1.08, 0.38],
        [1.0, 0.57],
        [0.42, 0.71],
        [-0.98, 0.74],
        [-1.07, 0.56],
      ],
      glass: [
        [0.42, 0.68],
        [0.1, 0.96],
        [-0.76, 0.98],
        [-0.98, 0.72],
      ],
      roof: [
        [0.12, 0.94],
        [0.08, 1],
        [-0.78, 1.01],
        [-0.79, 0.95],
      ],
      pillar: -0.22,
    },
    sedan: {
      len: 2.4,
      w: 0.9,
      r: 0.2,
      wz: [0.78, -0.8],
      body: [
        [-1.2, 0.18],
        [1.18, 0.18],
        [1.22, 0.4],
        [1.1, 0.58],
        [0.45, 0.68],
        [-0.6, 0.71],
        [-1.18, 0.64],
        [-1.22, 0.46],
      ],
      glass: [
        [0.45, 0.66],
        [0.15, 0.92],
        [-0.45, 0.94],
        [-0.75, 0.69],
      ],
      roof: [
        [0.17, 0.9],
        [0.13, 0.97],
        [-0.47, 0.98],
        [-0.47, 0.92],
      ],
      pillar: -0.12,
    },
    van: {
      len: 2.6,
      w: 1,
      r: 0.22,
      wz: [0.85, -0.85],
      body: [
        [-1.3, 0.18],
        [1.28, 0.18],
        [1.32, 0.5],
        [1.18, 0.68],
        [0.85, 1.22],
        [-1.3, 1.25],
      ],
      glass: [
        [1.17, 0.78],
        [0.87, 1.15],
        [0.55, 1.15],
        [0.55, 0.8],
      ],
      wide: true,
    },
    bus: {
      len: 6,
      w: 1.1,
      r: 0.26,
      wz: [2.1, -1.9],
      body: [
        [-3, 0.2],
        [3, 0.2],
        [3.02, 1.6],
        [2.9, 1.7],
        [-3, 1.7],
      ],
    },
  };
  cars = [];
}
